import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';

import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import {
    ActionGroup,
    ActionItem,
    BaseContainer,
    DialogConfig,
    DialogRef,
    EllipsisMultiActionItem,
    JobStatus,
    PaginatedBatch
} from '@stylehaven/components';

@Component({
    standalone: false,
    selector: 'digihaven-batch-error-dialog',
    templateUrl: './batch-error-dialog.component.html',
    styleUrls: ['./batch-error-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BatchErrorDialogComponent extends BaseContainer implements OnInit {
    public batch: PaginatedBatch;
    public status: JobStatus;

    public mailToServices$: Observable<string>;
    public adminEmail: string;
    public detailUrl: string;
    public viewMonitor: boolean;

    public actionItem$: Observable<ActionGroup>;

    constructor(public config: DialogConfig, public dialog: DialogRef<void>) {
        super();
        this.adminEmail = this.config.data?.adminEmail;
        this.detailUrl = this.config.data?.detailUrl;
        this.batch = this.config.data?.batch;
        this.status = this.config.data?.status;
        this.viewMonitor = this.config.data?.viewMonitor;
    }

    ngOnInit(): void {
        this.initMailToServicesStream();

        this.initActionItemStream();
    }

    private initMailToServicesStream(): void {
        const batchId: string = this.batch.Id;

        const subject = `${this.translateService.translate('batches.error-dialog.mail-to-services-subject', {
            batchId
        })}`;

        this.mailToServices$ = of(
            `mailto:${this.adminEmail}?subject=Issue report: ${subject}&body=${encodeURIComponent(this.detailUrl)}`
        );
    }

    private initActionItemStream(): void {
        const mailActionItem$: Observable<ActionItem> = this.mailToServices$.pipe(
            map((mail: string) => ({
                label: this.translateService.translate('actions.contact-admin'),
                eventListener: () => this.mailToServicesAction(mail)
            }))
        );

        this.actionItem$ = mailActionItem$.pipe(
            map((initialActions: ActionItem) => EllipsisMultiActionItem([initialActions])),
            map((ellipsisActions: ActionItem) => ({ actions: [ellipsisActions] }))
        );
    }

    private mailToServicesAction(mail: string): void {
        window.open(mail, '_blank', 'noopener, noreferrer');
    }

    public get errorMessage(): string {
        if (this.status === JobStatus.FAILED)
            return this.translateService.translate(`batches.error-dialog.error-message.Failed`, {
                amount: this.batch.Failed
            });
        if (this.status === JobStatus.INTERRUPTED)
            return this.translateService.translate(`batches.error-dialog.error-message.Interrupted`);
        return '';
    }
}
